// ─────────────────────────────────────────────────────────────────────────────
// CLASSIC VIEW  –  plain HTML fallback at /classic
// Recruiter-friendly, fully accessible, no canvas required.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './index.css';

type SkillGroup = {
  title: string;
  body: string;
};

const SKILLS: SkillGroup[] = [
  {
    title: 'Languages & Core Stack',
    body: 'Java, Kotlin, Python, Ruby, TypeScript/JavaScript, Go, Rust, C/C++, SQL, plus Spring Boot, Ruby on Rails, Django, Node, and React/Next.js when the UI needs a cape.',
  },
  {
    title: 'System Design & Backend',
    body: 'Distributed systems, microservices, event-driven Pub/Sub, REST/gRPC/GraphQL, caching, HA, retries, and the occasional “why is this service paging at 3am” investigation.',
  },
  {
    title: 'AI / ML',
    body: 'LLMs, RAG, LangChain, Whisper, agentic workflows, vector search, and prompt engineering: shipping features that talk back (politely).',
  },
  {
    title: 'Cloud & DevOps',
    body: 'AWS, GCP, Docker, Kubernetes, CI/CD, Firebase, Vercel, Railway. Moving millions of docs and events without drama (mostly).',
  },
  {
    title: 'Data & Messaging',
    body: 'PostgreSQL, MongoDB, Redis, Kafka, RabbitMQ, Pub/Sub. If it stores or streams, I’ve probably argued with it.',
  },
  {
    title: 'Mobile & Realtime',
    body: 'Flutter/Dart, React Native, WebSockets, WebRTC: from Bluetooth mesh chat to browser DAWs that somehow stay in sync.',
  },
];

type Project = {
  name: string;
  desc: string;
  link?: string;
  linkLabel?: string;
  gh?: string;
};

const PROJECTS: Project[] = [
  {
    name: 'DBSurfer',
    desc: 'Local, browser-based database client (think DBeaver or pgAdmin, but in your browser). Supports PostgreSQL, MySQL/MariaDB, SQL Server, SQLite, MongoDB, and Redis, with a schema browser, SQL generation, multi-tab editor, and optional AI Assist. Open source, MIT licensed.',
    gh: 'https://github.com/George-Freedland/db-surfer',
  },
  {
    name: 'LabSesh',
    desc: 'Your music lab in the browser: a collaborative DAW with devices and automation, pro DJ decks with waveforms and sync, and live jam rooms for up to 4 players over WebRTC. Live now.',
    link: 'https://www.labsesh.io',
    linkLabel: 'labsesh.io',
  },
  {
    name: 'AlphaLoom',
    desc: 'Solo-built AI trading platform: signals, agents, and live market dashboards. The markets never sleep; neither did the build logs.',
  },
  {
    name: 'Meshenger',
    desc: 'Bluetooth offline communication between any device. Flutter/Dart mesh messaging when the internet ghosts you.',
  },
  {
    name: 'Deadzone',
    desc: 'Online multiplayer survival game built with sockets and Three.js. Team up, loot, and try not to become someone else’s highlight reel.',
  },
  {
    name: 'Rhythm Wireless',
    desc: 'Platform for deploying Helium hotspot devices and sending crypto payments.',
    link: 'https://rhythmwireless.io/',
    linkLabel: 'rhythmwireless.io',
  },
  {
    name: 'streamcall.ai',
    desc: 'AI voice agents that link to CRMs to automate inbound and outbound sales calls.',
  },
  {
    name: 'seedhub.ai',
    desc: 'Marketplace connecting startup founders with early-stage investors. Next.js, Python, PostgreSQL.',
  },
  {
    name: 'Marble Adventure',
    desc: '3D marble platformer built with Three.js.',
    link: 'https://marble-adventure.vercel.app/',
    linkLabel: 'marble-adventure.vercel.app',
    gh: 'https://github.com/George-Freedland/marble-adventure',
  },
  {
    name: 'GMBGMM',
    desc: '3D video game built in C# on Torque3D, with 3,000+ downloads.',
    link: 'https://marbleblast.com/index.php/downloads/download/21-gmbgmm/9-gmbgmm',
    linkLabel: 'Download',
  },
  {
    name: 'TCP-IP Centralized Server',
    desc: 'TCP-IP Client and Server written in Python.',
    gh: 'https://github.com/George-Freedland/TCP-IP-Centralized-Server',
  },
];

function SkillsAccordion({ items }: { items: SkillGroup[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <ul className="classic-skills-accordion">
      {items.map((item, index) => {
        const open = openIndex === index;
        return (
          <li key={item.title} className={`classic-skill-item${open ? ' open' : ''}`}>
            <button
              type="button"
              className="classic-skill-header"
              aria-expanded={open}
              onClick={() => setOpenIndex(open ? null : index)}
            >
              <span>{item.title}</span>
              <span className="classic-skill-arrow" aria-hidden="true">
                {open ? '▾' : '▸'}
              </span>
            </button>
            {open && (
              <div className="classic-skill-body">
                <p>{item.body}</p>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export const Classic: React.FC = () => (
  <div className="classic-root">
    <header className="classic-header">
      <h1 className="classic-name">George Freedland</h1>
      <p className="classic-role">Software Engineer</p>
      <nav className="classic-nav" aria-label="External links">
        <a
          href="https://github.com/George-Freedland"
          target="_blank"
          rel="noopener noreferrer"
        >
          GitHub
        </a>
        <span aria-hidden="true">|</span>
        <a
          href="https://www.linkedin.com/in/george-freedland"
          target="_blank"
          rel="noopener noreferrer"
        >
          LinkedIn
        </a>
        <span aria-hidden="true">|</span>
        <a href="mailto:george.freedland@gmail.com">Email</a>
        <span aria-hidden="true">|</span>
        <Link to="/" className="classic-game-link">
          ↳ Play the platformer
        </Link>
      </nav>
    </header>

    <main className="classic-main">
      {/* Projects first */}
      <section aria-labelledby="projects-heading">
        <h2 id="projects-heading">Projects</h2>
        <ul className="classic-projects">
          {PROJECTS.map((p) => (
            <li key={p.name} className="classic-project-item">
              <strong>{p.name}</strong>
              <span>: {p.desc}</span>
              {p.link && (
                <>
                  {' '}
                  <a href={p.link} target="_blank" rel="noopener noreferrer">
                    {p.linkLabel ?? p.link}
                  </a>
                </>
              )}
              {p.gh && (
                <>
                  {' '}
                  <a href={p.gh} target="_blank" rel="noopener noreferrer">
                    GitHub
                  </a>
                </>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="about-heading">
        <h2 id="about-heading">About Me</h2>
        <p>
          Hi, I’m George. I ship backend systems for a living and side projects
          for the plot. By day I wrangle Spring Boot, Rails, Python services, and
          the occasional LLM that thinks it’s funny. By night I build things like
          browser music studios, AI trading desks, Bluetooth mesh chat, and
          multiplayer survival games, because apparently “relaxing” is not in my
          stack.
        </p>
        <p className="classic-about-gap">
          I’ve hopped from fintech to education to crypto startups without losing
          the plot: design it clean, ship it carefully, measure what matters.
          Also: music producer, outdoor enthusiast, and the kind of engineer who
          will absolutely name a microservice after a snack.
        </p>
      </section>

      <section aria-labelledby="skills-heading">
        <h2 id="skills-heading">Skills</h2>
        <p className="classic-skills-hint">Click a category to expand.</p>
        <SkillsAccordion items={SKILLS} />
      </section>

      <section aria-labelledby="exp-heading">
        <h2 id="exp-heading">Experience Highlights</h2>
        <ul className="classic-exp">
          <li>Software Engineer III @ Prosper: loan platform, LLM features, Pub/Sub at millions of events/day, 8M-doc GCS migration.</li>
          <li>Lead / sole engineer @ Rhythm Wireless: Next.js crypto analytics across ~5k wallets; mentored an intern into production.</li>
          <li>AI team @ Mirantis: RAG knowledge tools for ~150 employees; Whisper meeting summarization (~10 hrs/week saved).</li>
          <li>Udemy: subscription pricing (+20% conversion); transcoding pipelines for 100k+ hours of content.</li>
          <li>Prosper earlier chapters: Credit Card launch, Plaid upgrades, Spring Boot microservices across 10+ services.</li>
        </ul>
      </section>

      <section aria-labelledby="contact-heading">
        <h2 id="contact-heading">Contact</h2>
        <ul className="classic-contact">
          <li>
            Email:{' '}
            <a href="mailto:george.freedland@gmail.com">
              george.freedland@gmail.com
            </a>
          </li>
          <li>
            GitHub:{' '}
            <a
              href="https://github.com/George-Freedland"
              target="_blank"
              rel="noopener noreferrer"
            >
              github.com/George-Freedland
            </a>
          </li>
          <li>
            LinkedIn:{' '}
            <a
              href="https://www.linkedin.com/in/george-freedland"
              target="_blank"
              rel="noopener noreferrer"
            >
              linkedin.com/in/george-freedland
            </a>
          </li>
          <li>
            Website:{' '}
            <a
              href="https://www.georgefreedland.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              georgefreedland.com
            </a>
          </li>
        </ul>
      </section>
    </main>

    <footer className="classic-footer">
      <Link to="/" className="classic-game-link">
        ↳ Play the platformer version
      </Link>
    </footer>
  </div>
);
